// Public vendor registration page — `/join`.
// Stagers, photographers, inspectors, handymen, listing assistants, etc. fill
// out a single form to apply to the 6zero.ai marketplace. Submission lands as
// `status='draft'` and shows up in /admin → Vendors for approval.

import { useState } from "react";

const TYPES = [
  { group: "Prepare & Improve" },
  { id: "stager",             label: "Stager" },
  { id: "handyman",           label: "Handyman / small repairs" },
  { id: "painter",            label: "Painter" },
  { id: "cleaner",            label: "Cleaning service" },
  { id: "landscaper",         label: "Landscaper / curb appeal" },
  { id: "electrician",        label: "Electrician" },
  { id: "plumber",            label: "Plumber" },
  { id: "hvac",               label: "HVAC technician" },
  { id: "roofer",             label: "Roofer" },
  { id: "general_contractor", label: "General contractor" },
  { group: "Photography & Media" },
  { id: "photographer",       label: "Real-estate photographer" },
  { id: "videographer",       label: "Videographer / 3D tour" },
  { group: "Inspection & Assessment" },
  { id: "inspector",          label: "Home inspector" },
  { id: "pest_inspector",     label: "Pest / termite inspector" },
  { id: "appraiser",          label: "Real estate appraiser" },
  { group: "Finance & Legal" },
  { id: "lender",             label: "Mortgage broker / lender" },
  { id: "real_estate_attorney", label: "Real estate attorney" },
  { id: "notary",             label: "Notary" },
  { id: "title_escrow",       label: "Title / escrow company" },
  { group: "Professionals" },
  { id: "listing_assistant",  label: "Listing assistant (CA-licensed agent)" },
  { group: "Moving & Storage" },
  { id: "mover",              label: "Mover" },
  { id: "storage",            label: "Storage / pods" },
  { group: "Design" },
  { id: "interior_designer",  label: "Interior designer" },
];

const CSS = `
  *{box-sizing:border-box}
  body{margin:0;font-family:'Inter','Outfit',sans-serif;background:#eef3ef;color:#152d21}
  .v-page{min-height:100vh;display:flex;flex-direction:column}
  .v-top{display:flex;align-items:center;gap:14px;padding:12px 22px;border-bottom:1px solid rgba(45,122,98,0.15);background:#fff}
  .v-brand{display:flex;align-items:center;gap:10px;cursor:pointer}
  .v-brand-name{font-family:'DM Mono',monospace;font-size:13px;font-weight:700;color:#2b8259;letter-spacing:0.2em}
  .v-home{margin-left:auto;background:none;border:1px solid rgba(45,122,98,0.2);color:#5a8a78;border-radius:7px;padding:5px 10px;font-size:11px;cursor:pointer;font-family:'DM Mono',monospace}
  .v-home:hover{background:#1c2b25;color:#d4f5e8;border-color:#1c2b25}

  .v-hero{padding:48px 22px 24px;max-width:780px;margin:0 auto;width:100%}
  .v-pill{display:inline-flex;align-items:center;gap:7px;background:#e1eee6;border:1px solid #b2d4c0;border-radius:20px;padding:5px 14px;margin-bottom:18px}
  .v-pill-dot{width:6px;height:6px;border-radius:50%;background:#2b8259}
  .v-pill span{font-family:'DM Mono',monospace;font-size:10px;letter-spacing:0.14em;color:#2b8259;font-weight:700;text-transform:uppercase}
  .v-h1{font-family:'Playfair Display',serif;font-size:clamp(32px,5.5vw,46px);font-weight:700;color:#152d21;line-height:1.1;margin:0 0 14px;letter-spacing:-0.02em}
  .v-h1 em{color:#2b8259;font-style:italic;font-weight:500}
  .v-lead{font-size:16px;color:#5b7365;line-height:1.7;max-width:580px}
  .v-form{padding:8px 22px 60px;max-width:780px;margin:0 auto;width:100%}
  .v-card{background:#fff;border-radius:18px;padding:28px;box-shadow:0 6px 26px rgba(28,43,37,0.06);border:1px solid rgba(45,122,98,0.08)}
  .v-row{display:grid;grid-template-columns:1fr 1fr;gap:14px;margin-bottom:14px}
  .v-row.single{grid-template-columns:1fr}
  .v-label{display:block;font-size:11px;font-weight:700;color:#5b7365;letter-spacing:0.08em;text-transform:uppercase;margin-bottom:6px}
  .v-input,.v-textarea,.v-select{width:100%;background:#f7faf8;border:1.5px solid #d8ede6;color:#1c2b25;border-radius:10px;padding:11px 14px;font-size:14px;font-family:'Outfit',sans-serif;outline:none}
  .v-input:focus,.v-textarea:focus,.v-select:focus{border-color:#2d7a62}
  .v-input::placeholder,.v-textarea::placeholder{color:#9bbfb2}
  .v-textarea{min-height:90px;resize:vertical;line-height:1.55}
  .v-help{font-size:11px;color:#7aad98;margin-top:4px;font-family:'DM Mono',monospace;letter-spacing:0.04em}
  .v-submit{background:#2b8259;color:#fff;border:none;border-radius:12px;padding:14px 28px;font-size:15px;font-weight:700;cursor:pointer;font-family:'Outfit',sans-serif;box-shadow:0 6px 18px rgba(43,130,89,0.28);margin-top:8px;width:100%}
  .v-submit:hover:not(:disabled){background:#1c5448}
  .v-submit:disabled{opacity:0.6;cursor:default;box-shadow:none}
  .v-err{color:#c0354a;font-size:13px;font-family:'DM Mono',monospace;margin-top:12px}
  .v-success{padding:60px 22px;text-align:center;max-width:520px;margin:0 auto}
  .v-success h2{font-family:'Playfair Display',serif;font-size:32px;color:#152d21;margin:14px 0}
  .v-success p{color:#5b7365;font-size:15px;line-height:1.7}
  .v-check{width:64px;height:64px;border-radius:50%;background:#e4f7ef;border:2px solid #b8e8d4;display:flex;align-items:center;justify-content:center;font-size:28px;color:#2d7a62;margin:0 auto}
  .v-upload-zone{border:2px dashed #d8ede6;border-radius:12px;padding:24px 16px;text-align:center;cursor:pointer;transition:all .15s;background:#f7faf8;position:relative}
  .v-upload-zone:hover{border-color:#2b8259;background:#edf5f0}
  .v-upload-zone input[type=file]{position:absolute;inset:0;opacity:0;cursor:pointer;width:100%;height:100%}
  .v-upload-icon{font-size:28px;margin-bottom:6px;color:#9bbfb2}
  .v-upload-label{font-size:12px;color:#5b7365;font-family:'DM Mono',monospace}
  .v-photo-preview{width:110px;height:110px;border-radius:12px;object-fit:cover;border:2px solid #d8ede6;display:block}
  .v-photo-row{display:flex;align-items:center;gap:16px;flex-wrap:wrap}
  .v-portfolio-grid{display:grid;grid-template-columns:repeat(auto-fill,minmax(90px,1fr));gap:10px;margin-top:8px}
  .v-portfolio-thumb{position:relative;aspect-ratio:1;border-radius:10px;overflow:hidden;border:1.5px solid #d8ede6}
  .v-portfolio-thumb img{width:100%;height:100%;object-fit:cover;display:block}
  .v-portfolio-remove{position:absolute;top:3px;right:3px;background:rgba(21,45,33,0.75);color:#fff;border:none;border-radius:50%;width:20px;height:20px;cursor:pointer;font-size:11px;line-height:1;display:flex;align-items:center;justify-content:center}
  .v-portfolio-add{aspect-ratio:1;border:2px dashed #d8ede6;border-radius:10px;display:flex;flex-direction:column;align-items:center;justify-content:center;cursor:pointer;font-size:22px;color:#9bbfb2;background:#f7faf8;transition:all .15s;position:relative;overflow:hidden}
  .v-portfolio-add:hover{border-color:#2b8259;color:#2b8259}
  .v-portfolio-add input[type=file]{position:absolute;inset:0;opacity:0;cursor:pointer;width:100%;height:100%}

  @media(max-width:640px){
    .v-row{grid-template-columns:1fr}
    .v-hero{padding:32px 16px 16px}
    .v-form{padding:8px 16px 40px}
    .v-card{padding:20px 18px;border-radius:14px}
    .v-input,.v-textarea,.v-select{font-size:16px}
    .v-h1{font-size:30px}
  }
`;

const Logo = ({ size = 28 }) => (
  <svg width={size} height={size} viewBox="0 0 40 40" fill="none" aria-hidden="true">
    <rect width="40" height="40" rx="10" fill="#1c2b25" />
    <path d="M20 8L7 18V33H16V25H24V33H33V18L20 8Z" fill="#7ecfb0" />
    <rect x="17" y="25" width="6" height="8" fill="#1c2b25" opacity="0.4" />
  </svg>
);

const LANGUAGES = [
  "English","Spanish","Mandarin","Cantonese","Vietnamese",
  "Tagalog","Korean","Hindi","Arabic","French",
  "Portuguese","Russian","Japanese","Farsi","Hebrew",
];

const MAX_PORTFOLIO = 5;

async function uploadImage(file, purpose) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = async (e) => {
      try {
        const base64 = e.target.result.split(",")[1];
        const res = await fetch("/api/upload", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ data: base64, mime: file.type, purpose }),
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || "Upload failed");
        resolve(data.url);
      } catch (err) { reject(err); }
    };
    reader.onerror = () => reject(new Error("Could not read file"));
    reader.readAsDataURL(file);
  });
}

export default function VendorApply() {
  const [f, setF] = useState({
    type: "",
    name: "",
    blurb: "",
    contact_email: "",
    contact_phone: "",
    service_area_zips: "",
    pricing_text: "",
    lead_time_days: "",
    languages: [],
    photo_url: "",
    portfolio_images: [],
  });
  const [busy, setBusy] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [uploadingPortfolio, setUploadingPortfolio] = useState(false);
  const [err, setErr] = useState("");
  const [done, setDone] = useState(false);

  const update = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const toggleLang = (lang) => setF((s) => ({
    ...s,
    languages: s.languages.includes(lang)
      ? s.languages.filter((l) => l !== lang)
      : [...s.languages, lang],
  }));

  const handleProfilePhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingPhoto(true); setErr("");
    try {
      const url = await uploadImage(file, "profile");
      update("photo_url", url);
    } catch (ex) { setErr(ex.message); }
    setUploadingPhoto(false);
  };

  const handlePortfolioAdd = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const slots = MAX_PORTFOLIO - f.portfolio_images.length;
    const toUpload = files.slice(0, slots);
    setUploadingPortfolio(true); setErr("");
    try {
      const urls = await Promise.all(toUpload.map((file) => uploadImage(file, "portfolio")));
      setF((s) => ({ ...s, portfolio_images: [...s.portfolio_images, ...urls] }));
    } catch (ex) { setErr(ex.message); }
    setUploadingPortfolio(false);
  };

  const removePortfolioImage = (idx) =>
    setF((s) => ({ ...s, portfolio_images: s.portfolio_images.filter((_, i) => i !== idx) }));

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (!f.type) { setErr("Pick a service type."); return; }
    if (f.name.trim().length < 2) { setErr("Business / name is required."); return; }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(f.contact_email)) { setErr("Enter a valid email."); return; }
    setBusy(true);
    try {
      const res = await fetch("/api/vendors", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          action: "apply",
          type: f.type,
          name: f.name,
          blurb: f.blurb,
          contact_email: f.contact_email,
          contact_phone: f.contact_phone,
          service_area_zips: f.service_area_zips,
          pricing_text: f.pricing_text,
          lead_time_days: f.lead_time_days || null,
          languages: f.languages,
          photo_url: f.photo_url || null,
          portfolio: f.portfolio_images.map((url) => ({ url })),
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || `submit ${res.status}`);
      setDone(true);
    } catch (e) {
      setErr(e.message || "Couldn't submit. Try again.");
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <div className="v-page">
        <style>{CSS}</style>
        <header className="v-top">
          <div className="v-brand" onClick={() => (window.location.href = "/")}>
            <Logo />
            <div className="v-brand-name">6zero.ai</div>
          </div>
        </header>
        <div className="v-success">
          <div className="v-check">✓</div>
          <h2>Application received</h2>
          <p>
            Thanks for applying to the 6zero.ai marketplace. We review every application;
            you'll hear from us at <strong style={{ color: "#152d21" }}>{f.contact_email}</strong> once
            your profile is approved (typically within 2 business days).
          </p>
          <p style={{ marginTop: 24, display: "flex", gap: 10, justifyContent: "center", flexWrap: "wrap" }}>
            <button type="button" className="v-home" onClick={() => (window.location.href = `/vendor?email=${encodeURIComponent(f.contact_email)}`)}>
              Go to my vendor portal →
            </button>
            <button type="button" className="v-home" onClick={() => (window.location.href = "/")}>
              ← back to 6zero.ai
            </button>
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="v-page">
      <style>{CSS}</style>
      <header className="v-top">
        <div className="v-brand" onClick={() => (window.location.href = "/")}>
          <Logo />
          <div className="v-brand-name">6zero.ai</div>
        </div>
        <button type="button" className="v-home" onClick={() => (window.location.href = "/vendor")}>Vendor sign in</button>
        <button type="button" className="v-home" onClick={() => (window.location.href = "/")} style={{ marginLeft: 8 }}>← home</button>
      </header>

      <section className="v-hero">
        <div className="v-pill"><span className="v-pill-dot" /><span>Marketplace · for pros</span></div>
        <h1 className="v-h1">Join the 6zero.ai marketplace.<br /><em>Reach motivated sellers directly.</em></h1>
        <p className="v-lead">
          Sellers on 6zero.ai are pre-qualified (verified property, AI-priced listing) and pick their
          vendors stage-by-stage as they move toward close. No bidding wars, no race-to-the-bottom.
          You list your services + base pricing once; we generate a personalized quote per property.
        </p>
      </section>

      <form className="v-form" onSubmit={submit}>
        <div className="v-card">
          <div className="v-row">
            <div>
              <label className="v-label">Service type *</label>
              <select className="v-select" value={f.type} onChange={(e) => update("type", e.target.value)} required>
                <option value="">Pick one…</option>
                {(() => {
                  const out = [];
                  let group = null;
                  TYPES.forEach((t, i) => {
                    if (t.group) {
                      if (group) out.push(<optgroup key={`g${i}`} label={group.label}>{group.items}</optgroup>);
                      group = { label: t.group, items: [] };
                    } else if (group) {
                      group.items.push(<option key={t.id} value={t.id}>{t.label}</option>);
                    }
                  });
                  if (group) out.push(<optgroup key="glast" label={group.label}>{group.items}</optgroup>);
                  return out;
                })()}
              </select>
            </div>
            <div>
              <label className="v-label">Business / individual name *</label>
              <input className="v-input" value={f.name} onChange={(e) => update("name", e.target.value)} required placeholder="e.g. Aurora Real Estate Photography" />
            </div>
          </div>

          <div className="v-row single">
            <div>
              <label className="v-label">Short blurb</label>
              <textarea className="v-textarea" value={f.blurb} onChange={(e) => update("blurb", e.target.value)} placeholder="1–2 sentences. What's your specialty? What's your edge?" maxLength={400} />
              <div className="v-help">{400 - f.blurb.length} chars left</div>
            </div>
          </div>

          <div className="v-row">
            <div>
              <label className="v-label">Contact email *</label>
              <input className="v-input" type="email" value={f.contact_email} onChange={(e) => update("contact_email", e.target.value)} required placeholder="you@business.com" />
            </div>
            <div>
              <label className="v-label">Phone (optional)</label>
              <input className="v-input" value={f.contact_phone} onChange={(e) => update("contact_phone", e.target.value)} placeholder="(415) 555-0142" />
            </div>
          </div>

          <div className="v-row">
            <div>
              <label className="v-label">Service-area ZIPs *</label>
              <input className="v-input" value={f.service_area_zips} onChange={(e) => update("service_area_zips", e.target.value)} placeholder="94110, 94114, 94117" />
              <div className="v-help">comma-separated, US 5-digit only</div>
            </div>
            <div>
              <label className="v-label">Typical lead time (days)</label>
              <input className="v-input" type="number" min="0" value={f.lead_time_days} onChange={(e) => update("lead_time_days", e.target.value)} placeholder="3" />
            </div>
          </div>

          <div className="v-row single">
            <div>
              <label className="v-label">Pricing — describe in plain English</label>
              <textarea
                className="v-textarea"
                value={f.pricing_text}
                onChange={(e) => update("pricing_text", e.target.value)}
                placeholder={`e.g. "$395 for interior shoot up to 3000 sqft, +$195 for drone, +$99 for floor plan, 24h turnaround"`}
                maxLength={800}
              />
              <div className="v-help">★ AI converts this into structured pricing on submit — write naturally.</div>
            </div>
          </div>

          <div className="v-row single">
            <div>
              <label className="v-label">Languages spoken</label>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 4 }}>
                {LANGUAGES.map((lang) => (
                  <button
                    key={lang}
                    type="button"
                    onClick={() => toggleLang(lang)}
                    style={{
                      padding: "5px 12px", borderRadius: 20, fontSize: 12, cursor: "pointer", fontFamily: "'DM Mono',monospace",
                      background: f.languages.includes(lang) ? "#1c2b25" : "#f7faf8",
                      color: f.languages.includes(lang) ? "#d4f5e8" : "#5b7365",
                      border: f.languages.includes(lang) ? "1.5px solid #1c2b25" : "1.5px solid #d8ede6",
                    }}
                  >
                    {lang}
                  </button>
                ))}
              </div>
              <div className="v-help">select all that apply</div>
            </div>
          </div>

          <div className="v-row single">
            <div>
              <label className="v-label">Profile photo (optional)</label>
              <div className="v-photo-row">
                {f.photo_url
                  ? <img src={f.photo_url} alt="profile" className="v-photo-preview" />
                  : null}
                <label className="v-upload-zone" style={{ flex: 1, minWidth: 180 }}>
                  <input type="file" accept="image/jpeg,image/png,image/webp" onChange={handleProfilePhoto} disabled={uploadingPhoto} />
                  <div className="v-upload-icon">{uploadingPhoto ? "⏳" : f.photo_url ? "🔄" : "📷"}</div>
                  <div className="v-upload-label">
                    {uploadingPhoto ? "Uploading…" : f.photo_url ? "Change photo" : "Click to upload · JPEG / PNG / WebP · max 5 MB"}
                  </div>
                </label>
              </div>
            </div>
          </div>

          <div className="v-row single">
            <div>
              <label className="v-label">Portfolio photos (up to {MAX_PORTFOLIO})</label>
              <div className="v-portfolio-grid">
                {f.portfolio_images.map((url, i) => (
                  <div key={url} className="v-portfolio-thumb">
                    <img src={url} alt={`portfolio ${i + 1}`} />
                    <button type="button" className="v-portfolio-remove" onClick={() => removePortfolioImage(i)}>×</button>
                  </div>
                ))}
                {f.portfolio_images.length < MAX_PORTFOLIO && (
                  <label className="v-portfolio-add" title="Add photo">
                    <input type="file" accept="image/jpeg,image/png,image/webp" multiple onChange={handlePortfolioAdd} disabled={uploadingPortfolio} />
                    <span>{uploadingPortfolio ? "⏳" : "+"}</span>
                  </label>
                )}
              </div>
              <div className="v-help">show your best work — staging setups, before/after shots, job sites</div>
            </div>
          </div>

          <button type="submit" className="v-submit" disabled={busy || uploadingPhoto || uploadingPortfolio}>
            {busy ? "Submitting…" : "Submit application →"}
          </button>
          {err && <div className="v-err">{err}</div>}

          <p style={{ fontSize: 11, color: "#9aa9a0", marginTop: 18, fontFamily: "'DM Mono',monospace", letterSpacing: "0.04em", textAlign: "center" }}>
            Reviewed manually. No fees to list. We take a small cut of completed jobs through the platform.
          </p>
        </div>
      </form>
    </div>
  );
}
